import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { Close, Check, Add, Link } from '@mui/icons-material';
import { useDesignStore } from '../../context/DesignContext';
import { useSelectedDesignStore } from '../../context/SelectedDesignContext';
import { addNewAddition } from '../../services/designService';
import { useViewerStore } from 'context/ViewerContext';

type ImageGallerySpinnerProps = {
  name?: string;
  api: any;
  foot: number;
  onOpen?: () => void;
  onClose?: () => void;
};

const ImageGallerySpinner: React.FC<ImageGallerySpinnerProps> = ({
  name = 'Add New Additions',
  api,
  foot,
  onOpen,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [groups, setGroups] = useState<{ title: string; images: string[] }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const linkMode = useViewerStore((state) => state.linkMode);
  const toggleLinkMode = useViewerStore((state) => state.toggleLinkMode);
  
  const containerRef = useRef<HTMLDivElement>(null);

  const additionListNamesFromServer = useSelectedDesignStore((state) => state.additionListNamesFromServer);

  useEffect(() => {
    const loadImages = async () => {
      let imageNames = additionListNamesFromServer;
      
      if (!imageNames || imageNames.length === 0) {
        setGroups([]);
        return;
      }
      
      const formattedGroups = imageNames.map((group: any) => ({
        title: group.group,
        images: group.fileNames,
      }));
      
      setGroups(formattedGroups);
    };

    loadImages();
  }, [additionListNamesFromServer]);

  const handleLink = () => {
    toggleLinkMode();
  };

  const handleImageClick = (fileName: string) => {
    setSelectedImage(fileName);
  };

  const handleConfirm = async () => {
    if (!selectedImage) return;
    
    setIsLoading(true);
    
    try {
      if(useViewerStore.getState().linkMode){
        foot = 0; 
        const rightNewAddition = await addNewAddition(api, selectedImage, foot);
        if (rightNewAddition == null) { 
          console.error(`Failed to add addition to ${foot === 0 ? 'right' : 'left'} foot`);
          return; 
        }
        useDesignStore.getState().pushAdditionToDesign(rightNewAddition, foot);

        foot = 1; 
        const leftNewAddition = await addNewAddition(api, selectedImage, foot);
        if (leftNewAddition == null) { 
          console.error(`Failed to add addition to ${foot === 0 ? 'right' : 'left'} foot`);
          return; 
        }
        useDesignStore.getState().pushAdditionToDesign(leftNewAddition, foot);
      }
      else{
        const newAddition = await addNewAddition(api, selectedImage, foot);
        if (newAddition == null) { 
          console.error(`Failed to add addition to ${foot === 0 ? 'right' : 'left'} foot`);
          return; 
        }
        useDesignStore.getState().pushAdditionToDesign(newAddition, foot);
      }

    } catch (error) {
      console.error('Error adding addition:', error);
    } finally {
      setIsLoading(false);
      setIsEditing(false);
      setSelectedImage(null);
      onClose?.();
    }
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setIsEditing(false);
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setSelectedImage(null);
        setIsEditing(false);
        onClose?.();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isEditing, onClose]);

  useEffect(() => {
    if (isEditing) {
      onOpen?.();
      
      setTimeout(() => {
        if (containerRef.current) {
          const event = new CustomEvent('clampNewAddition', { 
            detail: { 
              target: containerRef.current,
              foot: foot 
            } 
          });
          document.dispatchEvent(event);
          console.log('Clamp event dispatched for new addition');
        }
      }, 100);
    }
  }, [isEditing, onOpen, foot]);

  if (!isEditing) {
    return (
      <Box
        onClick={() => {
          setIsEditing(true);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--toolbar-button-border)',
          borderRadius: '50%',
          bgcolor: 'var(--spinner-bg)',
          width: 26,
          height: 26,
          cursor: 'pointer',
          transition: '0.15s ease',
          '&:hover': { bgcolor: 'var(--spinner-hover-bg)' }
        }}
      >
        <Add sx={{ fontSize: 24, color: 'var(--toolbar-button-border)' }} />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        bgcolor: 'var(--spinner-bg)',
        backdropFilter: 'blur(8px)',
        borderRadius: '12px',
        border: '1px solid var(--toolbar-button-border)',
        padding: '6px 10px',
        width: '340px',
        maxHeight: '400px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'var(--spinner-hover-bg)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={24} sx={{ color: 'var(--toolbar-button-border)' }} />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid var(--toolbar-button-border)',
        }}
      >
        <Typography sx={{ fontSize: '10px', fontWeight: 500, color: 'var(--toolbar-button-border)', fontFamily: 'monospace' }}>
          {name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleLink}
            sx={{
              color: linkMode ? '#1976d2' : '#aaa',
              width: 20,
              height: 20,
              transition: '0.15s',
              '&:hover': {
                color: linkMode ? '#1565c0' : '#777',
              }
            }}
          >
            <Link sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} sx={{ color: '#d32f2f', width: 20, height: 20 }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={handleConfirm} 
            disabled={!selectedImage || isLoading}
            sx={{ color: selectedImage && !isLoading ? '#2e7d32' : '#aaa', width: 20, height: 20 }}
          >
            <Check sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--toolbar-button-border)',
            borderRadius: '4px',
          },
        }}
      >
        {groups.length === 0 ? (
          <Typography sx={{ textAlign: 'center', py: 2, color: 'var(--toolbar-button-border)', fontSize: '10px', fontFamily: 'monospace' }}>
            No additions available
          </Typography>
        ) : (
          groups.map((group, groupIdx) => (
            <Box key={groupIdx} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '9px',
                  fontWeight: 600,
                  color: 'var(--toolbar-button-border)',
                  fontFamily: 'monospace',
                  mb: 1,
                  pb: 0.5,
                  borderBottom: '1px solid var(--toolbar-button-border)',
                }}
              >
                {group.title}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {Array.from({ length: Math.ceil(group.images.length / 3) }).map((_, rowIdx) => {
                  const startIdx = rowIdx * 3;
                  const rowImages = group.images.slice(startIdx, startIdx + 3);
                  const paddedRow = [...rowImages];
                  while (paddedRow.length < 3) paddedRow.push(null);

                  return (
                    <Box
                      key={rowIdx}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-start',
                      }}
                    >
                      {paddedRow.map((fileName, idx) =>
                        fileName ? (
                          <Box
                            key={fileName}
                            onClick={() => handleImageClick(fileName)}
                            onDoubleClick={() => {
                              if (!isLoading) {
                                setSelectedImage(fileName);
                                handleConfirm();
                              }
                            }}
                            sx={{
                              width: '90px',
                              flexShrink: 0,
                              cursor: isLoading ? 'not-allowed' : 'pointer',
                              textAlign: 'center',
                              opacity: selectedImage === fileName ? 1 : 0.7,
                              transform: selectedImage === fileName ? 'scale(1.02)' : 'scale(1)',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <Box
                              sx={{
                                width: '90px',
                                height: '90px',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                border: selectedImage === fileName ? '2px solid var(--toolbar-button-border)' : '1px solid var(--toolbar-button-border)',
                                bgcolor: 'rgba(255,255,255,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={`/assets/Additions/${group.title}/${fileName}.png`}
                                alt={fileName}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '7px',
                                fontFamily: 'monospace',
                                color: 'var(--toolbar-button-border)',
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '90px',
                              }}
                            >
                              {fileName}
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            key={`empty-${idx}`}
                            sx={{
                              width: '90px',
                              flexShrink: 0,
                              opacity: 0,
                              pointerEvents: 'none',
                            }}
                          />
                        )
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ImageGallerySpinner;